import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const FIELDS = [
  ["interests", "What you're drawn to"],
  ["natural_strengths", "Naturally good at"],
  ["skills", "Current skills"],
  ["projects", "Things you've built"],
  ["goals", "Goals"],
  ["personality", "Personality"],
  ["constraints", "Real-life constraints"],
  ["life_context", "Life context"],
];

const EXAMPLES = {
  interests: ["how cities are designed", "true-crime podcasts", "restoring old furniture"],
  natural_strengths: ["spotting patterns nobody else sees", "calming people down", "explaining hard things simply"],
  skills: ["Python", "video editing", "conversational Spanish"],
  projects: ["a budgeting app for my family", "a zine about local bands", "rebuilt a bike from parts"],
  goals: ["earn from something I made", "study abroad next year", "work outdoors more"],
  personality: ["quietly competitive", "happier in small groups", "finishes what I start"],
  constraints: ["can't relocate this year", "no degree yet", "limited savings"],
  life_context: ["based in Manchester", "working full time", "about 6 hours free a week", "tight budget"],
};

const EMPTY = { summary: "", interests: [], natural_strengths: [], skills: [], projects: [], goals: [], personality: [], constraints: [], life_context: [] };

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/profile").then((p) => {
      setProfile(p ? { ...EMPTY, ...p } : null);
      setLoading(false);
    });
  }, []);

  const build = async () => {
    setBusy(true);
    try {
      setProfile({ ...EMPTY, ...(await api.post("/profile/synthesize")) });
      toast.success("Profile built");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async (next) => {
    setProfile(next);
    const { updated_at, domains, ...body } = next;
    await api.put("/profile", body);
  };

  const addItem = (key) => {
    const val = (drafts[key] || "").trim();
    if (!val) return;
    save({ ...profile, [key]: [...profile[key], val] });
    setDrafts({ ...drafts, [key]: "" });
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/paths/generate");
      track("profile_confirmed", { summary_length: profile.summary?.length || 0 });
      toast.success("Loop found some possibilities");
      navigate("/paths");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="overline" data-testid="profile-loading">Loading profile</p>;

  if (!profile)
    return (
      <div className="panel p-12 max-w-2xl" data-testid="profile-empty">
        <p className="overline">Step 02</p>
        <h1 className="mt-5 font-display text-4xl tracking-tight leading-none">No profile yet</h1>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          Answer a few interview questions first, then Loop can read your own words back to you.
        </p>
        <div className="mt-8 flex gap-3">
          <button onClick={() => navigate("/interview")} data-testid="go-interview-button"
            className="px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors duration-200">
            Go to interview
          </button>
          <button onClick={build} disabled={busy} data-testid="build-from-empty-button"
            className="px-6 py-3 rounded-full border border-white/12 hover:bg-white/[0.06] transition-colors duration-200">
            {busy ? "Working…" : "Build from transcript"}
          </button>
        </div>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="panel p-8 lg:p-10 rise">
        <p className="overline">Step 02 — Review what loop learned</p>
        <h1 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight leading-none">Your profile</h1>
        <textarea
          value={profile.summary}
          onChange={(e) => setProfile({ ...profile, summary: e.target.value })}
          onBlur={() => save(profile)}
          rows={3}
          data-testid="profile-summary-input"
          className="mt-7 w-full bg-transparent border border-white/10 rounded-sm p-5 font-display text-lg leading-relaxed outline-none focus:border-primary/60 transition-colors duration-200"
        />
        <p className="mt-3 text-xs text-muted-foreground">Change anything that doesn't sound like you. This drives everything next.</p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {FIELDS.map(([key, label], i) => (
          <div key={key} className="panel p-7 rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`field-${key}`}>
            <span className="overline">{label}</span>
            <div className="mt-5 flex flex-wrap gap-2">
              {profile[key].map((item) => (
                <span key={item} className="group flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border border-white/10 hover:border-white/25 transition-colors duration-200">
                  {item}
                  <button
                    onClick={() => save({ ...profile, [key]: profile[key].filter((x) => x !== item) })}
                    data-testid={`remove-${key}`}
                    aria-label={`Remove ${item}`}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-200"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
              {!profile[key].length && <span className="text-sm text-muted-foreground">Nothing captured yet.</span>}
            </div>
            <div className="mt-5 flex gap-2">
              <input
                value={drafts[key] || ""}
                onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && addItem(key)}
                placeholder={`Add your own — e.g. ${EXAMPLES[key][0]}`}
                data-testid={`add-input-${key}`}
                className="flex-1 bg-transparent border border-white/10 rounded-sm px-3 py-2 text-sm outline-none focus:border-primary/60 transition-colors duration-200"
              />
              <button onClick={() => addItem(key)} data-testid={`add-button-${key}`} aria-label={`Add to ${label}`}
                className="p-2 rounded-sm border border-white/10 hover:bg-white/[0.06] transition-colors duration-200">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`examples-${key}`}>
              <span className="text-xs text-muted-foreground">Examples:</span>
              {EXAMPLES[key].map((ex) => (
                <button
                  key={ex}
                  onClick={() => setDrafts({ ...drafts, [key]: ex })}
                  data-testid={`example-${key}`}
                  className="text-xs px-2.5 py-1 rounded-full border border-dashed border-white/15 text-muted-foreground hover:text-white hover:border-primary/50 transition-colors duration-200"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="panel p-8 flex flex-wrap items-center gap-6">
        <div>
          <p className="font-display text-lg">Does this feel accurate?</p>
          <p className="text-sm text-muted-foreground mt-1">Loop only recommends once you approve.</p>
        </div>
        <button onClick={confirm} disabled={busy} data-testid="confirm-profile-button"
          className="ml-auto flex items-center gap-3 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
          <Check className="h-4 w-4" />
          {busy ? "Exploring possibilities…" : "Looks right — show me paths"}
        </button>
      </div>
    </div>
  );
}
